import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { AboModal } from "@/components/account/AboModal";
import { DeleteAccountModal } from "@/components/account/DeleteAccountModal";
import { FeedbackSheet } from "@/components/FeedbackSheet";
import { consumerEntitlementsQueryKey } from "@/lib/consumerEntitlements";
import { deleteAccount } from "@/lib/deleteAccount";
import { consumerSubscriptionQueryKey } from "@/lib/stripe";
import { supabase } from "@/lib/supabase";

export function SubscriptionRequiredScreen() {
  const { t } = useTranslation("consumer");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  if (!user?.id) return null;
  const userId = user.id;
  const userEmail = user.email ?? "";

  function handleSubscriptionChange() {
    void queryClient.invalidateQueries({ queryKey: consumerEntitlementsQueryKey(userId) });
    void queryClient.invalidateQueries({ queryKey: consumerSubscriptionQueryKey(userId) });
  }

  async function handleDeleteAccount() {
    await deleteAccount();
    queryClient.clear();
    await supabase.auth.signOut();
    setDeleteAccountOpen(false);
    toast.success(t("settings.accountDeleted"));
    await router.navigate({ to: "/" });
  }

  return (
    <div className="relative flex min-h-[calc(100dvh-7rem)] flex-col items-center justify-center px-4 py-10">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-glow" />
      <div className="relative mb-6 w-full max-w-md text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-pulse/10 text-pulse">
          <Sparkles className="h-7 w-7" />
        </div>
        <h1 className="text-xl font-bold text-foreground">{t("subscription.requiredTitle")}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t("subscription.requiredBody")}
        </p>
      </div>
      <AboModal
        open
        required
        onOpenChange={(open) => {
          if (!open) handleSubscriptionChange();
        }}
        userId={userId}
        onContactSupport={() => setFeedbackOpen(true)}
        onManageAccount={() => {
          void router.navigate({ to: "/settings" });
        }}
        onDeleteAccount={() => setDeleteAccountOpen(true)}
      />
      <FeedbackSheet
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        defaultCategory="other"
        context={{ path: "subscription-required" }}
      />
      {userEmail ? (
        <DeleteAccountModal
          open={deleteAccountOpen}
          onOpenChange={setDeleteAccountOpen}
          userEmail={userEmail}
          onConfirmDelete={handleDeleteAccount}
        />
      ) : null}
    </div>
  );
}
